import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select.bootstrap5.css';

/**
 * Campo de multipla escolha, declarado na view com `data-selecao-multipla` e o
 * texto de vazio em `data-placeholder`.
 *
 * Por que tom-select e nao caixas de selecao: territorio de representante sao 27
 * estados, e em dois campos (atendidos e exclusivos) isso daria 54 caixas numa
 * tela que tem outros cinco campos. O <select multiple> nativo resolveria a
 * marcacao, mas so mostra uma janelinha rolavel, sem busca e sem o que ja foi
 * escolhido visivel de relance — e escolher um estado no meio de 27 vira caca ao
 * item. O tom-select mantem um <select> de verdade no DOM, entao o POST e o
 * `asp-for` continuam funcionando iguais, e acrescenta busca e as fichas do que
 * esta marcado.
 *
 * A busca cobre sigla e nome: o `value` da opcao e "PB" e o texto e
 * "PB — Paraíba", e quem digita "para" tem de achar o mesmo que quem digita
 * "pb".
 */
export function iniciarSelecoesMultiplas(escopo: ParentNode = document): void {
    escopo.querySelectorAll<HTMLSelectElement>('[data-selecao-multipla]').forEach(campo => {
        new TomSelect(campo, {
            plugins: ['remove_button'],
            placeholder: campo.dataset.placeholder,
            searchField: ['text', 'value'],
        });
    });
}

/**
 * Selecao unica com busca no servidor, declarada na view com
 * `data-selecao-remota` e a rota em `data-url`.
 *
 * Existe por causa dos 5.571 municipios: mandar todos para o navegador seriam
 * 400 KB por pagina de formulario, e um `<select>` nativo com 853 opcoes (Minas)
 * nao se navega. Entao a lista vem por UF, sob demanda, conforme a pessoa
 * digita.
 *
 * `data-depende-de` aponta para o campo que restringe a busca (a UF do
 * municipio, o representante do vendedor) e `data-parametro` diz com que nome
 * esse recorte vai na query string — `uf` quando nao se declara nada. Duas
 * coisas acontecem quando o campo-pai muda:
 *
 * 1. a escolha anterior e limpa, porque "Campina Grande" nao existe em Sergipe e
 *    deixar o valor antigo no campo gravaria um municipio de outro estado;
 * 2. o campo so fica habilitado com o campo-pai preenchido — sem UF a busca nao
 *    tem recorte e devolveria vazio, o que pareceria defeito.
 *
 * O `<select>` continua no DOM, entao `asp-for`, o POST e a validacao seguem
 * iguais — igual ao caso da selecao multipla.
 */
interface OpcaoRemota {
    valor: number;
    texto: string;
}

export function iniciarSelecoesRemotas(escopo: ParentNode = document): void {
    escopo.querySelectorAll<HTMLSelectElement>('[data-selecao-remota]').forEach(campo => {
        const url = campo.dataset.url;
        if (!url) return;

        const pai = campo.dataset.dependeDe
            ? document.querySelector<HTMLInputElement | HTMLSelectElement>(campo.dataset.dependeDe)
            : null;

        const valorDoPai = () => (pai?.value ?? '').trim();

        // O nome do parametro e da view, e nao fixo: o mesmo comportamento serve
        // municipio (`uf`) e vendedor (`representanteId`).
        const parametro = campo.dataset.parametro ?? 'uf';

        const selecao = new TomSelect(campo, {
            valueField: 'valor',
            labelField: 'texto',
            searchField: ['texto'],
            placeholder: campo.dataset.placeholder,
            maxOptions: 50,
            // Sem isto o `load` só dispara quando a pessoa digita, e quem apenas
            // abre o campo ve "No results found" com zero opcoes — parece
            // defeito. Com `focus`, clicar no campo ja traz a primeira leva.
            preload: 'focus',
            // O servidor ja devolve o recorte certo e ordenado; filtrar de novo
            // no navegador esconderia resultado quando o termo bate por acento.
            shouldLoad: () => valorDoPai().length > 0,
            load: async (termo: string, callback: (opcoes: OpcaoRemota[]) => void): Promise<void> => {
                const recorte = valorDoPai();

                if (!recorte) {
                    callback([]);
                    return;
                }

                try {
                    const resposta = await fetch(
                        `${url}?${parametro}=${encodeURIComponent(recorte)}` +
                        `&termo=${encodeURIComponent(termo)}`,
                        { headers: { 'Accept': 'application/json' } });

                    callback(resposta.ok ? await resposta.json() : []);
                } catch {
                    // Rede fora, sessao expirada, 500: o campo fica sem opcao, e
                    // nao quebrado. Estourar aqui derrubaria o formulario inteiro
                    // por causa de uma busca.
                    callback([]);
                }
            },
        });

        const semPai = campo.dataset.placeholderSemPai ?? campo.dataset.placeholder;
        const comPai = campo.dataset.placeholderComPai ?? 'Digite para buscar';

        const ajustarDisponibilidade = () => {
            const habilitado = valorDoPai().length > 0;

            if (habilitado) selecao.enable();
            else selecao.disable();

            // O texto de vazio tem de acompanhar o estado: deixar "Escolha a UF
            // primeiro" depois de a UF estar escolhida faz o campo parecer
            // travado quando ele ja esta pronto.
            selecao.settings.placeholder = habilitado ? comPai : semPai;
            selecao.inputState();
        };

        ajustarDisponibilidade();

        pai?.addEventListener('change', () => {
            // Limpa opcoes e valor: manter a escolha anterior deixaria o campo
            // exibindo uma cidade que nao pertence mais a UF selecionada, e o
            // servidor gravaria exatamente isso.
            selecao.clear(true);
            selecao.clearOptions();

            // E limpa o cache de buscas: o tom-select guarda o que ja pediu por
            // termo, e sem zerar isso a busca vazia de Sergipe devolveria a
            // lista do Mato Grosso que ficou em memoria.
            selecao.loadedSearches = {};

            ajustarDisponibilidade();

            // Recarrega agora, em vez de esperar o `preload` do proximo foco: ele
            // se desliga sozinho depois da primeira carga (`settings.preload =
            // false`), e religar o sinalizador nao bastou — medido. Chamar `load`
            // direto nao depende de estado interno da biblioteca, e ainda deixa a
            // lista pronta antes de a pessoa clicar.
            if (valorDoPai()) selecao.load('');
        });
    });
}
